/**
 * Дедупликатор подписок на WebSocket-события presence.
 *
 * Проблема: ChatListItem (список чатов) и ChatWindow (активный чат)
 * одновременно подписываются на /topic/presence.{userId} для одного и того же
 * пользователя — создаётся две одинаковые STOMP-подписки.
 *
 * Решение: единый менеджер подписок на уровне модуля.
 * На одного userId создаётся ровно один вызов subscribeToUserPresence,
 * и все зарегистрированные колбэки получают обновления.
 *
 * При отписке последнего колбэка подписка удаляется.
 */

import { subscribeToUserPresence } from '../api/websocket';

// userId -> { subscription: Object|null, callbacks: Set<Function> }
const subscriptions = new Map();

/**
 * Подписаться на обновления presence пользователя.
 * Если на этого userId уже есть подписка — просто добавляет колбэк.
 *
 * @param {string} userId — ID пользователя
 * @param {Function} callback — (presenceData) => void
 * @returns {{ unsubscribe: Function }} — объект для отписки
 */
export function subscribeToPresence(userId, callback) {
    if (!userId || typeof callback !== 'function') {
        return { unsubscribe: () => {} };
    }

    let entry = subscriptions.get(userId);
    if (!entry) {
        entry = { subscription: null, callbacks: new Set() };
        subscriptions.set(userId, entry);
        // Создаём реальную STOMP-подписку (может вернуть null, если WS не подключён)
        entry.subscription = subscribeToUserPresence(userId, (data) => {
            // Рассылаем данные всем зарегистрированным колбэкам
            entry.callbacks.forEach(cb => {
                try { cb(data); } catch (e) { console.error('[presenceSubscription] callback error:', e); }
            });
        });
    }

    entry.callbacks.add(callback);

    return {
        unsubscribe: () => {
            entry.callbacks.delete(callback);
            // Если больше нет колбэков — удаляем подписку
            if (entry.callbacks.size === 0) {
                try {
                    entry.subscription?.unsubscribe?.();
                } catch (e) {
                    // ignore — подписка могла быть уже отменена
                }
                subscriptions.delete(userId);
            }
        }
    };
}

/**
 * Очистка всех подписок (например, при logout).
 */
export function clearAllPresenceSubscriptions() {
    subscriptions.forEach((entry) => {
        try {
            entry.subscription?.unsubscribe?.();
        } catch (e) {
            // ignore
        }
    });
    subscriptions.clear();
}